import { ThemeProvider } from "@emotion/react";
import Cookies from "js-cookie";
import { useState } from "react";
import { Route, Routes } from "react-router-dom";

import MyState from "./context/state";
import ScrollToTop from "./ScrollToTop";

// MUI
import { Info } from "@mui/icons-material";
import { Alert, Snackbar, createTheme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// PAGES
import Contact from "./pages/contact";
import CourseDetails from "./pages/course/details";
import Enquiry from "./pages/course/enquiry";
import CourseReceipt from "./pages/course/receipt";
import CoursesList from "./pages/courses";
import GalleryPublic from "./pages/gallery";
import Header from "./pages/header";
import Home from "./pages/home";
import Homework from "./pages/homework";
import HomeworkDetails from "./pages/homework/details";
import InstallApp from "./pages/light-app";
import Reviews from "./pages/reviews";
import SharePublic from "./pages/share";
import Verification from "./pages/verification";
import VerifiedCertificate from "./pages/verify";
import Workshop from "./public/workshop-form";

// Controller
import Batch from "./controller/batch/index";
import CourseContent from "./controller/course-content";
import CourseSubject from "./controller/course-subject";
import CourseUpload from "./controller/course-upload";
import CourseAdd from "./controller/course/add";
import CoursesPage from "./controller/course/index";
import CourseUpdate from "./controller/course/update";
import DashboardController from "./controller/dashboard";
import DashboardFranchise from "./controller/dashboard-franchise";
import EnquiryAdmin from "./controller/enquiry";
import EnquiryEdit from "./controller/enquiry/edit";
import Franchises from "./controller/franchise";
import FranchiseUpload from "./controller/franchise-upload";
import Gallery from "./controller/gallery";
import HeroSliderAdmin from "./controller/hero-slider";
import HomeworkAssignments from "./controller/homework/assignments";
import HomeworkSubmissions from "./controller/homework/index";
import HomeworkSubjects from "./controller/homework/subjects";
import MessageAdmin from "./controller/message";
import NoticeAdmin from "./controller/notice";
import NotificationAdmin from "./controller/notification";
import PaymentReport from "./controller/payment/report";
import ReferrerDetails from "./controller/referrer/view";
import ReviewAdmin from "./controller/review";
import SidebarController from "./controller/sidebar";
import Students from "./controller/student";
import StudentInfo from "./controller/student-info";
import StudentMarks from "./controller/student-marks";
import StudentUpload from "./controller/student-upload";
import TeacherAdmin from "./controller/teacher";
import WorkshopData from "./controller/workshop";
import FranchiseRole from "./controller/roles";
import FranchiseReset from "./login//franchise/reset";
import AdminLogin from "./login/admin/index";
import AdminReset from "./login/admin/reset";
import FranchiseLogin from "./login/franchise/index";

// User
import SidebarUser from "./student/sidebar";

// Payment
import Payment from "./controller/payment";

// Attendance
import AttendanceFranchise from "./controller/attendance";
import IDCard from "./controller/attendance/idcard";
import Scanner from "./controller/attendance/scanner";

// PUBLIC
import PaymentReceipt from "./pages/payment-receipt";
import ComputerCertificate from "./public/certificate";
import ComputerMarksheet from "./public/marksheet";
import TermsAndConditions from "./public/terms&conditions";
import TypingCertificate from "./public/typing-certificate";

// Student
import { Toaster } from "react-hot-toast";
import StudentLogin from "./login/student";
import StudentReset from "./login/student/reset";
import AdmissionStudent from "./pages/admission";
import StudentHome from "./student";
import AttendanceStudent from "./student/attendance";
import PaymentStudent from "./student/payment";
import Profile from "./student/profile";
import RewardsStudent from "./student/rewards";

function App() {
	const theme = createTheme({
		breakpoints: {
			values: {
				xs: 0,
				sm: 600,
				md: 900,
				lg: 1300,
				xl: 1920,
			},
		},
		palette: {
			primary: {
				light: "#1a5f6a",
				main: "#0E3A43",
				dark: "#082329",
				contrastText: "#ffffff",
			},
			white: {
				main: "#ffffff",
			},
			secondary: {
				main: "#4dd0e1",
			},
			error: {
				main: "#ef5350",
			},
			info: {
				main: "#26a69a",
			},
			success: {
				main: "#66bb6a",
			},
			warning: {
				main: "#ffa726",
			},
			background: {
				default: "#f5f5f5",
				paper: "#ffffff",
			},
			text: {
				primary: "#000000",
				secondary: "#1a5f6a",
			},
		},
		typography: {
			button: {
				textTransform: "initial",
			},
		},

		components: {
			MuiButton: {
				styleOverrides: {
					root: {
						boxShadow: "none",
					},
				},
			},
		},
	});

	const fullscreen = useMediaQuery(useTheme().breakpoints.down("md"));

	const [open, setOpen] = useState(false);
	const [message, setMessage] = useState("");
	const handlesnackbar = (data) => {
		setMessage(data);
		setOpen(true);
	};

	const auth = Cookies.get("authuser");

	// APIS
	const apilogin = "/spywareassets/api/login/index.php";
	const apiadmin = "/spywareassets/api/admin/index.php";
	const apiglobal = "/spywareassets/api/global/index.php";
	const apipublic = "/spywareassets/api/public/index.php";

	const stuimg = "/spywareassets/student/";
	const gallery = "/spywareassets/gallery/";

	return (
		<MyState>
			<ScrollToTop />
			<Toaster />
			<ThemeProvider theme={theme}>
				<Snackbar open={open} onClose={() => setOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
					<Alert icon={<Info />} severity="success" variant="filled" onClose={() => setOpen(false)}>
						{message}
					</Alert>
				</Snackbar>
				<Routes>
					<Route path="/" element={<Header />}>
						<Route path="/" element={<Home snackbar={handlesnackbar} />} />
						<Route path="/verification" element={<Verification apipublic={apipublic} snackbar={handlesnackbar} />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="/courses" element={<CoursesList />} />
						<Route path="/courses/details" element={<CourseDetails />} />
						<Route path="/courses/enquiry" element={<Enquiry />} />
						<Route path="/courses/receipt" element={<CourseReceipt />} />
						<Route path="/verify" element={<VerifiedCertificate apipublic={apipublic} snackbar={handlesnackbar} stuimg={stuimg} />} />
						<Route path="/gallery" element={<GalleryPublic apipublic={apipublic} gallery={gallery} />} />
						<Route path="/share" element={<SharePublic />} />
						<Route path="/workshop" element={<Workshop snackbar={handlesnackbar} />} />
						<Route path="/terms&conditions" element={<TermsAndConditions />} />
						<Route path="/reviews" element={<Reviews />} />
						<Route path="/payment-receipt" element={<PaymentReceipt />} />
						<Route path="homework" element={<Homework />} />
						<Route path="homework/:submissionid" element={<HomeworkDetails />} />

						{/* Student */}
						<Route path="admission" element={<AdmissionStudent snackbar={handlesnackbar} />} />
					</Route>

					<Route path="/login-admin" element={<AdminLogin apiadminlogin={apilogin} snackbar={handlesnackbar} />} />
					<Route path="/reset-admin" element={<AdminReset apilogin={apilogin} snackbar={handlesnackbar} />} />
					<Route path="/login-student" element={<StudentLogin snackbar={handlesnackbar} />} />

					<Route path="/login-franchise" element={<FranchiseLogin apilogin={apilogin} snackbar={handlesnackbar} />} />
					<Route path="/reset-franchise" element={<FranchiseReset apilogin={apilogin} snackbar={handlesnackbar} />} />
					<Route path="/reset-student" element={<StudentReset snackbar={handlesnackbar} />} />
					<Route path="/light-app" element={<InstallApp />} />

					<Route path="/public/computer-certificate/:studentid/:courseid" element={<ComputerCertificate apipublic={apipublic} />} />
					<Route path="/public/computer-marksheet/:studentid/:courseid" element={<ComputerMarksheet apipublic={apipublic} />} />
					<Route path="/public/typing-certificate/:studentid/:courseid" element={<TypingCertificate apipublic={apipublic} />} />

					<Route path="/controller" element={<SidebarController apiglobal={apiglobal} />}>
						<Route path="dashboard" element={<DashboardController apiadmin={apiadmin} snackbar={handlesnackbar} />} />
						<Route path="dashboard-franchise" element={<DashboardFranchise apiglobal={apiglobal} />} />
						<Route path="franchises/*" element={<Franchises snackbar={handlesnackbar} apiadmin={apiadmin} />} />
						<Route path="franchise-upload/:franchiseid" element={<FranchiseUpload snackbar={handlesnackbar} apiadmin={apiadmin} />} />
						{/* <Route path="courses" element={<Courses apiadmin={apiadmin} />} /> */}
						<Route path="course-upload/:courseid" element={<CourseUpload apiadmin={apiadmin} snackbar={handlesnackbar} />} />
						<Route path="course-subject" element={<CourseSubject apiadmin={apiadmin} snackbar={handlesnackbar} />} />
						<Route path="students" element={<Students auth={auth} apiglobal={apiglobal} />} />
						<Route path="student-upload/:studentid" element={<StudentUpload auth={auth} apiglobal={apiglobal} apiadmin={apiadmin} snackbar={handlesnackbar} />} />
						<Route path="student-info/:studentid" element={<StudentInfo apiadmin={apiadmin} apiglobal={apiglobal} snackbar={handlesnackbar} auth={auth} fullscreen={fullscreen} />} />
						<Route path="student-marks/:studentid/:courseid" element={<StudentMarks apiglobal={apiglobal} snackbar={handlesnackbar} auth={auth} />} />
						<Route path="gallery" element={<Gallery apiadmin={apiadmin} snackbar={handlesnackbar} gallery={gallery} />} />
						<Route path="batch" element={<Batch snackbar={handlesnackbar} />} />
						<Route path="workshop" element={<WorkshopData snackbar={handlesnackbar} />} />
						<Route path="payment-report" element={<PaymentReport />} />
						<Route path="website-course" element={<CourseContent snackbar={handlesnackbar} />} />
						<Route path="teacher" element={<TeacherAdmin snackbar={handlesnackbar} />} />
						<Route path="review" element={<ReviewAdmin snackbar={handlesnackbar} />} />
						<Route path="message" element={<MessageAdmin snackbar={handlesnackbar} />} />
						<Route path="notice" element={<NoticeAdmin snackbar={handlesnackbar} />} />
						<Route path="notification" element={<NotificationAdmin snackbar={handlesnackbar} />} />
						<Route path="hero-slider" element={<HeroSliderAdmin snackbar={handlesnackbar} />} />

						{/* Home Work */}
						<Route path="homework" element={<HomeworkSubmissions snackbar={handlesnackbar} />} />
						<Route path="homework/assignments" element={<HomeworkAssignments snackbar={handlesnackbar} />} />
						<Route path="homework/subjects" element={<HomeworkSubjects snackbar={handlesnackbar} />} />

						<Route path="courses" element={<CoursesPage />} />
						<Route path="courses/add" element={<CourseAdd snackbar={handlesnackbar} />} />
						<Route path="courses/update" element={<CourseUpdate snackbar={handlesnackbar} />} />

						<Route path="enquiry" element={<EnquiryAdmin snackbar={handlesnackbar} />} />
						<Route path="enquiry/edit" element={<EnquiryEdit snackbar={handlesnackbar} />} />

						{/* Attendance */}
						<Route path="attendance" element={<AttendanceFranchise apiglobal={apiglobal} />} />

						{/* Payment */}
						<Route path="payment" element={<Payment snackbar={handlesnackbar} />} />

						{/* Referrer */}
						<Route path="referrer/view" element={<ReferrerDetails apiglobal={apiglobal} apiadmin={apiadmin} snackbar={handlesnackbar} />} />

						{/* Franchise Roles */}
						<Route path="franchise-roles" element={<FranchiseRole snackbar={handlesnackbar} />} />
					</Route>

					{/* Student */}
					<Route path="/student" element={<SidebarUser />}>
						<Route path="home" element={<StudentHome snackbar={handlesnackbar} />} />
						<Route path="payment" element={<PaymentStudent />} />
						<Route path="attendance" element={<AttendanceStudent />} />
						<Route path="profile" element={<Profile snackbar={handlesnackbar} />} />
						<Route path="rewards" element={<RewardsStudent />} />
					</Route>

					{/* Attendance */}
					<Route path="/attendance/scanner" element={<Scanner />} />
					<Route path="/idcard/:studentid/:courseid" element={<IDCard />} />
				</Routes>
			</ThemeProvider>
		</MyState>
	);
}

export default App;
